import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Check, Zap, FileText, Mail, Link2, Download, History, BarChart2, Loader2 } from 'lucide-react';
import { createCheckoutSession } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const PRO_FEATURES = [
  { icon: FileText,  text: 'Unlimited invoices every month' },
  { icon: History,   text: 'Full invoice history & records' },
  { icon: Mail,      text: 'Email invoices directly to clients' },
  { icon: Link2,     text: 'Shareable invoice links' },
  { icon: Download,  text: 'CSV export & bulk PDF download' },
  { icon: BarChart2, text: 'Unlimited business profiles' },
];

export default function UpgradeModal({ onClose, reason, initialBillingCycle }) {
  const [billingCycle, setBillingCycle] = useState(initialBillingCycle || 'monthly');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const { user } = useAuth();
  const navigate = useNavigate();

  const isYearly   = billingCycle === 'yearly';
  const monthlyPrice = '$6';
  const yearlyPrice  = '$60';
  const monthlyEquiv = '$5/mo';

  const handleUpgrade = async () => {
    if (!user) {
      sessionStorage.setItem('pendingUpgradePlan', billingCycle);
      navigate('/auth?next=upgrade');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const { checkout_url } = await createCheckoutSession(billingCycle);
      window.location.href = checkout_url;
    } catch (err) {
      setError(err.message || 'Could not start checkout. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      <div className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden">

        {/* Top accent */}
        <div className="h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />

        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/40 flex items-center justify-center">
              <Zap className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Upgrade to Pro</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Unlock your full workflow</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Reason callout */}
        {reason && (
          <div className="mx-6 mb-4 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
            <p className="text-sm text-amber-800 dark:text-amber-300 font-medium">{reason}</p>
          </div>
        )}

        {/* Billing interval toggle */}
        <div className="mx-6 mb-4">
          <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-xl p-1 gap-1">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all ${
                !isYearly
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all flex items-center justify-center gap-2 ${
                isYearly
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Yearly
              <span className="text-xs font-bold text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-1.5 py-0.5 rounded-full">
                Save 17%
              </span>
            </button>
          </div>
        </div>

        {/* Features */}
        <ul className="px-6 pb-5 space-y-3">
          {PRO_FEATURES.map(({ icon: Icon, text }) => (
            <li key={text} className="flex items-center gap-3">
              <div className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center flex-shrink-0">
                <Check className="w-3 h-3 text-blue-600 dark:text-blue-400" />
              </div>
              <span className="text-sm text-gray-700 dark:text-gray-300">{text}</span>
            </li>
          ))}
        </ul>

        {/* Pricing + CTA */}
        <div className="px-6 pb-6 space-y-3">

          {/* Price display */}
          <div className="flex items-baseline gap-2">
            {isYearly ? (
              <>
                <span className="text-3xl font-black text-gray-900 dark:text-white">{yearlyPrice}</span>
                <span className="text-sm text-gray-400">/year</span>
                <span className="ml-auto text-xs font-semibold text-green-600 dark:text-green-400">
                  {monthlyEquiv} · save $12
                </span>
              </>
            ) : (
              <>
                <span className="text-3xl font-black text-gray-900 dark:text-white">{monthlyPrice}</span>
                <span className="text-sm text-gray-400">/month</span>
                <span className="ml-auto text-xs text-gray-400">billed monthly</span>
              </>
            )}
          </div>

          {error && (
            <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <button
            onClick={handleUpgrade}
            disabled={loading}
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-xl transition-colors shadow-md shadow-blue-600/20 flex items-center justify-center gap-2"
          >
            {loading ? (
              <><Loader2 size={16} className="animate-spin" /> Preparing checkout…</>
            ) : (
              <><Zap size={16} /> Upgrade to Pro: {isYearly ? yearlyPrice + '/yr' : monthlyPrice + '/mo'}</>
            )}
          </button>

          <p className="text-center text-xs text-gray-400 dark:text-gray-500">
            Secure checkout · Cancel anytime · No hidden fees
          </p>
        </div>
      </div>
    </div>
  );
}
